import PropTypes from "prop-types";
import { Truck, PackageCheck, AlertTriangle, RefreshCw, Sparkles } from "lucide-react";
import StatusBadge from "./StatusBadge.jsx";

/**
 * ShippingEstimate — resumen del envío físico (MVP-02A).
 *
 * Muestra el concepto público «Envío» como un único importe y el intervalo de
 * entrega estimado, siempre con lenguaje de estimación y nunca de garantía.
 * Cubre todos los estados del cálculo server-side: sin dirección, dirección
 * incompleta, cargando, disponible, digital sin envío, indisponible (CP no
 * cubierto / sin tarifa / sin embalaje), caducada tras un cambio, y error de
 * red con reintento.
 *
 * El coste, el peso y el servicio los fija siempre el backend: este componente
 * solo presenta lo que devuelve la cotización. No permite editar importes.
 *
 * Props:
 *   state        — "idle" | "loading" | "available" | "digital" | "unavailable" | "expired" | "error"
 *   cost         — importe de «Envío» (string/number), en estado available
 *   currency     — código de moneda (default "EUR")
 *   deliveryMin  — fecha ISO mínima estimada (opcional)
 *   deliveryMax  — fecha ISO máxima estimada (opcional)
 *   message      — texto explicativo para unavailable/expired/error
 *   onRetry      — callback del botón de recalcular/reintentar (opcional)
 *   formatCurrency — (value, currency) => string
 */

const DOW = ["dom.", "lun.", "mar.", "mié.", "jue.", "vie.", "sáb."];
const MONTH = ["ene.", "feb.", "mar.", "abr.", "may.", "jun.", "jul.", "ago.", "sep.", "oct.", "nov.", "dic."];

function formatEsDate(iso) {
    if (!iso) return null;
    const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
    if (Number.isNaN(d.getTime())) return null;
    return `${DOW[d.getDay()]} ${d.getDate()} ${MONTH[d.getMonth()]}`;
}

const defaultFmt = (v, c = "EUR") =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: c }).format(Number(v) || 0);

export default function ShippingEstimate({
    state = "idle",
    cost = null,
    currency = "EUR",
    deliveryMin = null,
    deliveryMax = null,
    message = "",
    onRetry = null,
    formatCurrency = defaultFmt,
    className = "",
}) {
    const retryButton = (label, tone = "neutral") =>
        onRetry ? (
            <button
                type="button"
                onClick={onRetry}
                className={
                    "mt-2 inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium " +
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 " +
                    "focus-visible:ring-offset-base-100 transition-colors " +
                    (tone === "error"
                        ? "border-error/50 text-error hover:bg-error/10"
                        : "border-base-content/30 text-base-content hover:bg-base-content/10")
                }
            >
                <RefreshCw className="w-3.5 h-3.5" aria-hidden /> {label}
            </button>
        ) : null;

    // --- Sin dirección todavía ---
    if (state === "idle") {
        return (
            <div className={`flex justify-between items-center text-sm text-base-content/60 ${className}`}>
                <span className="inline-flex items-center gap-1.5">
                    <Truck className="w-4 h-4" aria-hidden /> Envío
                </span>
                <span className="italic text-xs">Indica tu dirección para calcularlo</span>
            </div>
        );
    }

    if (state === "incomplete") {
        return (
            <div role="alert" className={`rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm ${className}`}>
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 text-warning" aria-hidden />
                    <span className="font-medium text-base-content">Dirección incompleta</span>
                </div>
                <p className="mt-1 text-xs text-base-content/70">
                    {message || "Selecciona una dirección completa para calcular el envío."}
                </p>
            </div>
        );
    }

    // --- Calculando ---
    if (state === "loading") {
        return (
            <div className={`flex justify-between items-center text-sm ${className}`} aria-live="polite">
                <span className="inline-flex items-center gap-1.5 text-base-content/70">
                    <Truck className="w-4 h-4" aria-hidden /> Envío
                </span>
                <span
                    className="h-4 w-24 rounded bg-base-content/15 motion-safe:animate-pulse"
                    aria-label="Calculando el envío"
                />
            </div>
        );
    }

    // --- Pedido digital: sin envío ---
    if (state === "digital") {
        return (
            <div className={`flex justify-between items-center text-sm ${className}`}>
                <span className="inline-flex items-center gap-1.5 text-base-content/70">
                    <Sparkles className="w-4 h-4" aria-hidden /> Envío
                </span>
                <span className="text-base-content/70">Sin envío (pedido digital)</span>
            </div>
        );
    }

    // --- Indisponible / caducada / error ---
    if (state === "unavailable" || state === "expired" || state === "error") {
        const tone = state === "expired" ? "warning" : "error";
        const label =
            state === "expired" ? "Estimación caducada"
            : state === "error" ? "No se pudo calcular"
            : "Envío no disponible";
        return (
            <div
                role="alert"
                className={
                    "rounded-xl border p-3 text-sm " +
                    (tone === "warning"
                        ? "border-warning/40 bg-warning/10"
                        : "border-error/40 bg-error/10") +
                    " " + className
                }
            >
                <div className="flex items-center gap-2">
                    <AlertTriangle
                        className={`w-4 h-4 shrink-0 ${tone === "warning" ? "text-warning" : "text-error"}`}
                        aria-hidden
                    />
                    <span className="font-medium text-base-content">{label}</span>
                </div>
                <p className="mt-1 text-xs text-base-content/70">
                    {message ||
                        (state === "expired"
                            ? "Tu pedido cambió; vuelve a calcular el envío antes de continuar."
                            : "No hay una tarifa de envío disponible para este pedido ahora mismo.")}
                </p>
                {retryButton(state === "expired" ? "Recalcular" : "Reintentar", tone === "error" ? "error" : "neutral")}
            </div>
        );
    }

    // --- Disponible (físico) ---
    const min = formatEsDate(deliveryMin);
    const max = formatEsDate(deliveryMax);
    let intervalText = null;
    if (min && max) intervalText = min === max ? `hacia el ${max}` : `entre el ${min} y el ${max}`;
    else if (max) intervalText = `hacia el ${max}`;

    return (
        <div className={`flex flex-col gap-1.5 text-sm ${className}`}>
            <div className="flex justify-between items-center">
                <span className="inline-flex items-center gap-1.5 text-base-content/70">
                    <Truck className="w-4 h-4" aria-hidden /> Envío
                </span>
                <span className="font-medium">{formatCurrency(cost, currency)}</span>
            </div>
            {intervalText && (
                <div className="flex items-start gap-1.5 text-xs text-base-content/60">
                    <PackageCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden />
                    <span>
                        Entrega estimada {intervalText}.{" "}
                        <StatusBadge label="estimación" tone="info" size="xs" outline className="align-middle" />
                    </span>
                </div>
            )}
        </div>
    );
}

ShippingEstimate.propTypes = {
    state: PropTypes.oneOf(["idle", "incomplete", "loading", "available", "digital", "unavailable", "expired", "error"]),
    cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    deliveryMin: PropTypes.string,
    deliveryMax: PropTypes.string,
    message: PropTypes.string,
    onRetry: PropTypes.func,
    formatCurrency: PropTypes.func,
    className: PropTypes.string,
};
